/**
 * Security utilities for API request signing and encryption
 */

/**
 * @param {string} message
 * @param {string} secretKey
 * @returns {Promise<string>}
 */
async function hmacSha256(message, secretKey) {
	const subtle = globalThis.crypto?.subtle;
	if (subtle) {
		const encoder = new TextEncoder();
		const keyData = encoder.encode(secretKey);
		const messageData = encoder.encode(message);

		const key = await subtle.importKey(
			'raw',
			keyData,
			{ name: 'HMAC', hash: 'SHA-256' },
			false,
			['sign']
		);

		const signature = await subtle.sign('HMAC', key, messageData);
		return Array.from(new Uint8Array(signature))
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('');
	}

	const { createHmac } = await import('crypto');
	return createHmac('sha256', secretKey).update(message).digest('hex');
}

/**
 * Generate HMAC-SHA256 signature for API requests
 * @param {string} timestamp - Unix timestamp
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} path - Request path
 * @param {string} body - Request body (empty for GET)
 * @param {string} nonce - Request nonce
 * @param {string} secretKey - API signature secret
 * @returns {Promise<string>} Hex-encoded signature
 */
export async function generateSignature(timestamp, method, path, body = '', nonce = '', secretKey) {
	const message = `${timestamp}${method.toUpperCase()}${path}${body}${nonce}`;
	return hmacSha256(message, secretKey);
}

/**
 * Sign API request with timestamp and signature headers
 * @param {string} method - HTTP method
 * @param {string} path - Request path
 * @param {object|null} body - Request body object
 * @param {string} secretKey - API signature secret
 * @returns {Promise<object>} Headers object with signature
 */
export async function signRequest(method, path, body = null, secretKey) {
	const timestamp = Math.floor(Date.now() / 1000).toString();
	const bodyString = body ? JSON.stringify(body) : '';
	const nonce = await generateNonce();

	const signature = await generateSignature(timestamp, method, path, bodyString, nonce, secretKey);

	return {
		'X-Timestamp': timestamp,
		'X-Signature': signature,
		'X-Nonce': nonce
	};
}

/**
 * Secure storage wrapper for sensitive data
 * Uses sessionStorage with encryption
 */
export class SecureStorage {
	/**
	 * Store encrypted data in sessionStorage
	 * @param {string} key - Storage key
	 * @param {any} value - Value to store
	 */
	static set(key, value) {
		try {
			const encrypted = this.simpleEncrypt(JSON.stringify(value));
			sessionStorage.setItem(key, encrypted);
		} catch (error) {
			console.error('SecureStorage set error:', error);
		}
	}

	/**
	 * Retrieve and decrypt data from sessionStorage
	 * @param {string} key - Storage key
	 * @returns {any} Decrypted value
	 */
	static get(key) {
		try {
			const encrypted = sessionStorage.getItem(key);
			if (!encrypted) return null;

			const decrypted = this.simpleDecrypt(encrypted);
			return JSON.parse(decrypted);
		} catch (error) {
			console.error('SecureStorage get error:', error);
			return null;
		}
	}

	/**
	 * Remove data from sessionStorage
	 * @param {string} key - Storage key
	 */
	static remove(key) {
		sessionStorage.removeItem(key);
	}

	/**
	 * Clear all data from sessionStorage
	 */
	static clear() {
		sessionStorage.clear();
	}

	/**
	 * Simple XOR encryption (for basic obfuscation)
	 * Note: For production, use a proper encryption library
	 * @param {string} text - Text to encrypt
	 * @returns {string} Encrypted text (base64)
	 */
	static simpleEncrypt(text) {
		// Simple XOR cipher with a key
		const key = 'uzakitu-secret-key-2024'; // Should be from env
		let encrypted = '';

		for (let i = 0; i < text.length; i++) {
			const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
			encrypted += String.fromCharCode(charCode);
		}

		return btoa(encrypted); // Base64 encode
	}

	/**
	 * Simple XOR decryption
	 * @param {string} encryptedText - Encrypted text (base64)
	 * @returns {string} Decrypted text
	 */
	static simpleDecrypt(encryptedText) {
		const key = 'uzakitu-secret-key-2024';
		const encrypted = atob(encryptedText); // Base64 decode
		let decrypted = '';

		for (let i = 0; i < encrypted.length; i++) {
			const charCode = encrypted.charCodeAt(i) ^ key.charCodeAt(i % key.length);
			decrypted += String.fromCharCode(charCode);
		}

		return decrypted;
	}
}

/**
 * Sanitize input to prevent XSS attacks
 * @param {string} input - User input
 * @returns {string} Sanitized input
 */
export function sanitizeInput(input) {
	if (typeof input !== 'string') return input;

	const element = document.createElement('div');
	element.innerText = input;
	return element.innerHTML;
}

/**
 * Validate email format
 * @param {string} email - Email address
 * @returns {boolean} True if valid
 */
export function validateEmail(email) {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
}

/**
 * Validate phone number (basic)
 * @param {string} phone - Phone number
 * @returns {boolean} True if valid
 */
export function validatePhone(phone) {
	const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
	return phoneRegex.test(phone);
}

/**
 * Generate a random nonce for CSRF protection
 * @returns {Promise<string>} Random nonce
 */
export async function generateNonce() {
	const cryptoObj = globalThis.crypto;
	if (cryptoObj?.getRandomValues) {
		const array = new Uint8Array(16);
		cryptoObj.getRandomValues(array);
		return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
	}

	const { randomBytes } = await import('crypto');
	return randomBytes(16).toString('hex');
}

/**
 * Rate limiter for client-side request throttling
 */
export class ClientRateLimiter {
	constructor() {
		/** @type {Map<string, number[]>} */
		this.requests = new Map();
	}

	/**
	 * Check if request is allowed based on rate limit
	 * @param {string} key - Identifier (e.g., endpoint)
	 * @param {number} limit - Max requests allowed
	 * @param {number} windowMs - Time window in milliseconds
	 * @returns {boolean} True if request is allowed
	 */
	checkLimit(key, limit = 10, windowMs = 60000) {
		const now = Date.now();
		const requestLog = this.requests.get(key) || [];

		// Remove old requests outside the window
		const validRequests = requestLog.filter((timestamp) => now - timestamp < windowMs);

		if (validRequests.length >= limit) {
			return false;
		}

		// Add current request
		validRequests.push(now);
		this.requests.set(key, validRequests);

		return true;
	}

	/**
	 * Clear rate limit history for a key
	 * @param {string} key - Identifier
	 */
	clear(key) {
		this.requests.delete(key);
	}

	/**
	 * Clear all rate limit history
	 */
	clearAll() {
		this.requests.clear();
	}
}

/**
 * Mask sensitive data for display
 * @param {string} data - Sensitive data
 * @param {number} visibleChars - Number of characters to show
 * @returns {string} Masked data
 */
export function maskSensitiveData(data, visibleChars = 4) {
	if (!data || data.length <= visibleChars) return data;

	const visible = data.slice(-visibleChars);
	const masked = '*'.repeat(data.length - visibleChars);
	return masked + visible;
}

/**
 * Check if running in secure context (HTTPS)
 * @returns {boolean} True if secure
 */
export function isSecureContext() {
	return window.isSecureContext || window.location.protocol === 'https:';
}

/**
 * Content Security Policy violation reporter
 * @param {object} violationReport - CSP violation report
 */
export function reportCSPViolation(violationReport) {
	// Log CSP violations to backend for monitoring
	console.error('CSP Violation:', violationReport);

	// Optionally send to backend endpoint
	// fetch('/api/security/csp-report', {
	//     method: 'POST',
	//     body: JSON.stringify(violationReport)
	// });
}

// Setup CSP violation reporting
if (typeof document !== 'undefined') {
	document.addEventListener('securitypolicyviolation', (e) => {
		reportCSPViolation({
			documentURI: e.documentURI,
			violatedDirective: e.violatedDirective,
			effectiveDirective: e.effectiveDirective,
			originalPolicy: e.originalPolicy,
			blockedURI: e.blockedURI,
			statusCode: e.statusCode
		});
	});
}

export default {
	generateSignature,
	signRequest,
	SecureStorage,
	sanitizeInput,
	validateEmail,
	validatePhone,
	generateNonce,
	ClientRateLimiter,
	maskSensitiveData,
	isSecureContext,
	reportCSPViolation
};
